import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Building2, Clock } from 'lucide-react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Organization } from '../../types/admin';

// ── Helpers ──────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  trialing: 'bg-sky-50 text-sky-700',
  past_due: 'bg-amber-50 text-amber-700',
  canceled: 'bg-rose/10 text-rose',
  incomplete: 'bg-stone text-slate',
  unpaid: 'bg-rose/10 text-rose',
};

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatCardCurrency(amount: number, currency: string): string {
  const sym = currency === 'eur' ? '€' : currency === 'gbp' ? '£' : '$';
  return `${sym}${(amount / 100).toFixed(2)}`;
}

function daysUntil(unixSeconds: number): number {
  return Math.max(0, Math.ceil((unixSeconds * 1000 - Date.now()) / (1000 * 60 * 60 * 24)));
}

// ── Org Card ───────────────────────────────────────────────────────────────

function OrgCard({ org, onClick }: { org: Organization; onClick: () => void }) {
  const stripe = org.stripe;
  const cost = org.subscriptionCost;
  const isFlatRate = !!(cost?.nickname?.toLowerCase().includes('flat'));
  const isFree = cost?.totalAmount === 0;
  const isTrialing = stripe?.status === 'trialing';
  const trialDaysLeft = isTrialing && stripe?.trialEnd ? daysUntil(stripe.trialEnd) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-full text-left bg-white border border-stone rounded-xl p-4 shadow-brand-sm
                 flex items-center gap-3
                 hover:border-plum/40 hover:shadow-brand-md active:bg-mist
                 transition-all duration-150"
    >
      {/* Logo */}
      {org.logoBase64 ? (
        <img
          src={org.logoBase64}
          alt={org.name}
          className="shrink-0 w-10 h-10 rounded-lg object-contain border border-stone bg-mist p-0.5"
        />
      ) : (
        <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
          <span className="font-display font-bold text-base text-plum">
            {org.name.charAt(0).toUpperCase()}
          </span>
        </div>
      )}

      {/* Left: name + status + plan info */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-small text-midnight truncate">{org.name}</p>
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          {stripe ? (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold
                          uppercase tracking-wide leading-none
                          ${STATUS_STYLES[stripe.status] ?? 'bg-stone text-slate'}`}
            >
              {statusLabel(stripe.status)}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold
                            uppercase tracking-wide leading-none bg-stone text-slate">
              No subscription
            </span>
          )}
          {stripe?.productName && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                            uppercase tracking-wide leading-none text-plum bg-plum/5 border border-plum/20">
              {stripe.productName}
            </span>
          )}
          {isFree && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                            uppercase tracking-wide leading-none text-green-700 bg-green-50 border border-green-200">
              Free
            </span>
          )}
        </div>
        {trialDaysLeft !== null && (
          <p className={`text-[11px] mt-0.5 flex items-center gap-1 ${trialDaysLeft <= 3 ? 'text-red-600 font-semibold' : 'text-sky-700'}`}>
            <Clock size={10} className="shrink-0" />
            {trialDaysLeft === 0 ? 'Trial expires today' : `${trialDaysLeft}d left in trial`}
          </p>
        )}
      </div>

      {/* Right: active users + cost */}
      <div className="shrink-0 text-right">
        <p className="text-[13px] font-semibold text-midnight">
          {org.activeUserCount ?? 0} {(org.activeUserCount ?? 0) === 1 ? 'user' : 'users'}
        </p>
        {cost ? (
          isFree ? (
            <p className="text-[12px] text-green-700 font-medium mt-0.5">Free</p>
          ) : (
            <p className="text-[12px] text-slate mt-0.5">
              {formatCardCurrency(cost.totalAmount, cost.currency)}/{cost.interval === 'year' ? 'yr' : 'mo'}
              {isFlatRate && <span className="text-[10px] text-slate/70 block">flat rate</span>}
            </p>
          )
        ) : (
          <p className="text-[12px] text-slate mt-0.5">—</p>
        )}
      </div>

      <ChevronRight size={16} className="shrink-0 text-slate" />
    </button>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function ManageOrganizationsPage() {
  const navigate = useNavigate();
  const { orgs, loading, error } = useOrganizations();

  // Always alphabetical
  const sortedOrgs = useMemo(
    () => [...orgs].sort((a, b) => a.name.localeCompare(b.name)),
    [orgs],
  );

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-5xl md:mx-auto">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-h1 text-midnight mb-1">Organizations</h1>
          <p className="text-small text-slate">All companies with IOMI app accounts.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/platform-tools/organizations/new')}
          className="shrink-0 h-10 px-4 rounded-lg bg-plum text-white text-[13px] font-semibold
                     flex items-center gap-2
                     hover:bg-rose active:opacity-80 transition-colors duration-150"
        >
          <Plus size={16} />
          New Org
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load organizations</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && sortedOrgs.length === 0 && (
        <div className="flex flex-col items-center py-20 gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-plum/10 flex items-center justify-center">
            <Building2 size={28} className="text-plum" />
          </div>
          <div>
            <p className="font-semibold text-small text-midnight">No organizations yet</p>
            <p className="text-[13px] text-slate mt-1">
              Tap "New Org" to create the first one.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && sortedOrgs.length > 0 && (
        <ul className="flex flex-col gap-3 md:grid md:grid-cols-2 lg:grid-cols-3">
          {sortedOrgs.map(org => (
            <li key={org.id}>
              <OrgCard
                org={org}
                onClick={() => navigate(`/admin/platform-tools/organizations/${org.id}`)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
